import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  CardContent,
  CardHeader,
  Divider,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_STAFF_DETAILS } from "../../../../apis/organization-apis/people-apis/peopleApis";
import TableSkeleton from "../../../../components/Skeletons/TableSkeleton";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import InformationStepsCard from "../../../../components/InformationStepsCard";
import AccessSummary from "./features/AccessSummary";
import SystemsTable from "./components/SystemsTable";
import SystemsHeader from "./components/SystemsHeader";
import SystemsFilters from "./components/SystemsFilters";
import SystemsEmptyState from "./components/SystemsEmptyState";
import AddCriticalSystemDialog from "./features/AddCriticalSystemDialog";
import { useNavigate } from "react-router-dom";
import useSortSystemsByDueDate from "../shared/hooks/useSortSystemsByDueDate";
import { useSystemsData } from "./hooks/useSystemsData";
import { useSystemFilters } from "./hooks/useSystemFilters";
import { useSystemGrouping } from "./hooks/useSystemGrouping";
import { useSystemActions } from "./hooks/useSystemActions";
import { useTheme } from "@mui/material";

const SystemsTab = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [showArchivedSystems, setShowArchivedSystems] = useState(false);
  const [addSystemDialogOpen, setAddSystemDialogOpen] = useState(false);

  const { data: staffData } = useQuery(GET_STAFF_DETAILS);
  const employees = staffData?.getEmployees?.data || [];

  const filters = useSystemFilters();
  const {
    criticalSystems,
    systemAdmins,
    loadingCriticalSystems,
    refetchCriticalSystems,
  } = useSystemsData(filters.filterObject);
  
  const groupedSystems = useSystemGrouping(
    criticalSystems,
    employees,
    showArchivedSystems
  );

  const {
    snackbarInfo,
    setSnackbarInfo,
    archivingSystemId,
    loadingCreateManualSystem,
    handleArchiveSystem,
    manualCreateCriticalSystem,
  } = useSystemActions(refetchCriticalSystems);

  const filteredSystems = useMemo(() => {
    let filtered = groupedSystems.filter((system) => {
      if (system.isGroupHeader) {
        return (system.groupAccounts || []).some(acc => {
          const isActive = acc.is_review_active !== false;
          return showArchivedSystems ? !isActive : isActive;
        });
      }
      const isActive = system.is_review_active !== false;
      return showArchivedSystems ? !isActive : isActive;
    });
    
    const visibleGroupKeys = new Set(filtered.filter(s => s.isGroupHeader).map(s => s.groupKey));
    filtered = filtered.filter(s => !s.isGroupChild || visibleGroupKeys.has(s.groupKey));
    
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase().trim();
      filtered = filtered.filter(s => {
        const name = (s.name || '').toLowerCase();
        const accountLabel = (s.account_label || '').toLowerCase();
        return name.includes(query) || accountLabel.includes(query);
      });
    }
    
    return filtered;
  }, [groupedSystems, filters.searchQuery, showArchivedSystems]);

  const sortedSystems = useSortSystemsByDueDate(filteredSystems);

  const totalAccountCount = useMemo(() => {
    return sortedSystems.filter(s => !s.isGroupHeader).length;
  }, [sortedSystems]);

  useEffect(() => {
    try {
      const lastAccessedId = localStorage.getItem("accessManagementLastAccessedAccount");
      if (!lastAccessedId) return;

      const lastIdNumber = Number(lastAccessedId);
      if (!lastIdNumber) return;

      const matchingAccount = criticalSystems.find(
        (s) => !s.isGroupHeader && Number(s.id) === lastIdNumber
      );

      if (matchingAccount && matchingAccount.is_review_active === false && !showArchivedSystems) {
        setShowArchivedSystems(true);
      }
    } catch (error) {
      console.error("Failed to restore last accessed account tab state:", error);
    }
  }, [criticalSystems, showArchivedSystems]);

  const handleConnectIntegration = () => {
    navigate('/integrations');
  };

  const handleAddSystem = () => {
    setAddSystemDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setAddSystemDialogOpen(false);
  };

  const handleSubmitSystem = async (systemData) => {
    const success = await manualCreateCriticalSystem(systemData);
    if (success) {
      setAddSystemDialogOpen(false);
    }
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
      <Box sx={{ p: 1, minHeight: "100vh" }}>
        <Box sx={{ mb: 2 }}>
          <InformationStepsCard
            heading="**What should I do on this page?**"
            subtitle="This page helps you manage all your critical systems and their access reviews."
            steps={[
              "Click **\"Add New Account\"** to add new systems manually or connect them via integrations (like AWS, GitHub, Slack).",
              
              "For each account, set the **account owner**, **criticality level**, and **review cycle** to ensure proper access management.",
              
              "Systems can have multiple accounts - expand a system to see all its accounts grouped together.",
              
              "Use **filters** to quickly find accounts by owner, criticality, or integration source.",
              
              "Click **\"View Details\"** on any system to manage users, start access reviews, and see detailed information.",
              
              "**Archive** accounts that are no longer in use to keep your list clean and organized.",
            ]}
            footerText="💡 Regular access reviews help maintain security and compliance by ensuring users only have the access they need."
            collapsible={true}
            defaultExpanded={false}
          />
        </Box>
                
        <AccessSummary systems={criticalSystems} loading={loadingCriticalSystems} />
        
        <Card sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
        }}>
          <CardHeader
            title={
              <SystemsHeader
                showArchivedSystems={showArchivedSystems}
                setShowArchivedSystems={setShowArchivedSystems}
                archivingSystemId={archivingSystemId}
                hasActiveFilters={filters.hasActiveFilters}
                activeFiltersCount={filters.activeFiltersCount}
                onAddSystem={handleAddSystem}
              />
            }
            sx={{
              borderBottom: "1px solid #ddd",
              background: "#F7F7FC",
              ".MuiCardHeader-title": {
                fontSize: "16px",
                fontWeight: "bold",
              },
              padding: "14px",
            }}
          />

          <CardContent sx={{ p: 0 }}>
            <SystemsFilters
              filters={filters}
              systemAdmins={systemAdmins}
            />

            <Divider sx={{ borderColor: theme.customColors.border.main }} />

            <Box sx={{ 
              px: 2,
              py: 1.5,
              mb: 1
            }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                {totalAccountCount} account{totalAccountCount !== 1 ? 's' : ''} found
              </Typography>
            </Box>

            <Box>
              {loadingCriticalSystems ? (
                <TableSkeleton />
              ) : criticalSystems.length === 0 ? (
                <SystemsEmptyState onAddSystem={handleAddSystem} />
              ) : (
                <SystemsTable
                  systems={sortedSystems}
                  onArchiveSystem={handleArchiveSystem}
                  archivingSystemId={archivingSystemId}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      <AddCriticalSystemDialog
        open={addSystemDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitSystem}
        loading={loadingCreateManualSystem}
        onConnectIntegration={handleConnectIntegration}
      />
    </>
  );
};

export default SystemsTab;
